import { useSelector } from "react-redux";
import { useEffect } from "react";
import { Box } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import NavigationHeader from "../../components/CommonComponents/Navigation";
import ScrollToTopWrapper from "../../components/CommonComponents/ScrollToTopWrapper";
import { ROUTE_PATH } from "../../constants/route";
import Analytics from "../../components/Analytics";
import SEO_CONFIG from "../../constants/seoConfig";
import FooterNew from "../../components/CommonComponents/FooterNew";
import { LOCAL_BUSINESS_SCHEMA, ABOUT_PAGE_SCHEMA } from "../../constants/schemaConfig";

const PublicLayout = ({ children, privateRoute = false }) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isMobileView = searchParams.get("mobile") === "true";
  const authentication = useSelector((state) => state.authentication);
  const navigate = useNavigate();

  useEffect(() => {
    const token =
      authentication?.auth?.data?.auth_token ||
      authentication?.auth?.data?.data?.auth_token ||
      authentication?.userData?.data?.auth_token ||
      null;

      if (!token && privateRoute) {
        if (![ROUTE_PATH.LOGIN, ROUTE_PATH.SIGNUP].includes(location?.pathname))
        navigate(ROUTE_PATH.HOME);
      }

  }, [authentication, privateRoute, navigate, location?.pathname]);

  // Get SEO data for current route
  const currentPath = location.pathname;
  const seoData = SEO_CONFIG[currentPath];

  // Determine which schema to render based on the current path
  const getSchema = () => {
    // Check if current path matches about page
    if (currentPath === ROUTE_PATH.ABOUT) {
      return ABOUT_PAGE_SCHEMA;
    }
    // Default to LocalBusiness schema for all other pages
    return LOCAL_BUSINESS_SCHEMA;
  };

  const schema = getSchema();

  // Update schema script tag when route changes
  useEffect(() => {
    // Remove existing schema script tags
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts?.forEach(script => {
      // Only remove our schema scripts (not other JSON-LD scripts that might exist)
      const scriptContent = script.textContent || script.innerHTML;
      if (scriptContent.includes('"@type":"LocalBusiness"') || scriptContent.includes('"@type":"AboutPage"')) {
        script.remove();
      }
    });

    // Create and append new schema script
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'wanderobe-schema';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    // Cleanup function
    return () => {
      const scriptToRemove = document.getElementById('wanderobe-schema');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [currentPath, schema]);

  return (
    <ScrollToTopWrapper>
      <Helmet>
        <title>{seoData?.title || 'Wanderobe'}</title>
        {seoData?.description && (
          <meta name="description" content={seoData.description} />
        )}
      </Helmet>
      <Analytics />
        {!isMobileView && <NavigationHeader privateRoute={privateRoute} />}
        <Box sx={{minHeight: '70dvh'}}>
          {children}
        </Box>
        {!isMobileView && <FooterNew />}
        <Outlet />
    </ScrollToTopWrapper>)
};

export default PublicLayout;